import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Activity, MapPin, CalendarClock, Wrench, ArrowUpRight, Cpu, Flame } from 'lucide-react';
import Reveal from './Reveal';
import Counter from './Counter';

// Finseo-style bento grid with mini-UI mockups, adapted to Closefire (dark fire theme).
export default function BentoGrid() {
  return (
    <section className="py-28 bg-[#05070d]">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal className="max-w-2xl mb-14">
          <div className="section-eyebrow fire-text"><Cpu className="w-4 h-4 text-primary" /> The Complete Platform</div>
          <h2 className="font-heading font-extrabold text-4xl md:text-5xl mt-4 text-white tracking-tight">Everything you need to stay fire-safe. In one place.</h2>
          <p className="mt-4 text-lg text-gray-400">From certified equipment to 24/7 monitored systems, installation and lifetime maintenance \u2014 Closefire covers every angle of fire protection.</p>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-3 auto-rows-[minmax(200px,auto)] gap-5">
          {/* Big monitored-status card */}
          <Reveal className="lg:col-span-2 lg:row-span-2" delay={0}>
            <div className="relative h-full glass glass-hover rounded-3xl p-7 overflow-hidden">
              <div className="orb w-[280px] h-[280px] bg-primary/15 -top-20 -right-10" />
              <div className="relative">
                <div className="flex items-center gap-2 text-xs font-heading font-bold uppercase tracking-wider text-gray-500"><Activity className="w-4 h-4 text-primary" /> Live System Status</div>
                <h3 className="font-heading font-bold text-2xl text-white mt-3">24/7 Monitored Protection</h3>
                <p className="text-sm text-gray-400 mt-2 max-w-md">Detection, alarm and suppression systems watched round the clock \u2014 so threats are caught before they spread.</p>

                <div className="mt-6 space-y-2.5">
                  {[['Fire Alarm Panel', 'Online', true], ['Hydrant Pump', 'Online', true], ['Sprinkler Network', 'Armed', true], ['CO2 Suppression', 'Standby', true]].map(([name, status, ok]) => (
                    <div key={name} className="flex items-center justify-between bg-white/[0.03] border border-white/8 rounded-xl px-4 py-3">
                      <span className="text-sm text-gray-200 font-medium">{name}</span>
                      <span className="flex items-center gap-2 text-xs font-heading font-semibold text-emerald-400">
                        <span className="w-2 h-2 rounded-full bg-emerald-400 animate-glow" /> {status}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="mt-6 flex items-end justify-between">
                  <div>
                    <div className="font-display font-extrabold text-5xl fire-text"><Counter to={99} suffix="%" /></div>
                    <div className="text-xs text-gray-500 font-heading font-semibold uppercase tracking-wide">Uptime coverage</div>
                  </div>
                  <Link to="/products" className="inline-flex items-center gap-1.5 text-primary font-semibold text-sm">Explore systems <ArrowUpRight className="w-4 h-4" /></Link>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Certified */}
          <Reveal delay={80}>
            <div className="h-full glass glass-hover rounded-3xl p-7 flex flex-col">
              <span className="grid place-items-center w-12 h-12 rounded-2xl bg-gradient-to-br from-primary to-accent text-white"><ShieldCheck className="w-6 h-6" /></span>
              <h3 className="font-heading font-bold text-xl text-white mt-5">ISI-Certified Equipment</h3>
              <p className="text-sm text-gray-400 mt-2 flex-1">Every extinguisher is ISI-marked as per IS 15683, with CE, UL & NFPA compliant systems.</p>
              <div className="flex flex-wrap gap-1.5 mt-4">
                {['ISI', 'CE', 'UL', 'ISO'].map((b) => <span key={b} className="px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 text-xs font-display font-bold text-white/80">{b}</span>)}
              </div>
            </div>
          </Reveal>

          {/* Pan-India */}
          <Reveal delay={160}>
            <div className="h-full glass glass-hover rounded-3xl p-7 flex flex-col">
              <span className="grid place-items-center w-12 h-12 rounded-2xl bg-white/10 text-primary"><MapPin className="w-6 h-6" /></span>
              <h3 className="font-heading font-bold text-xl text-white mt-5">Pan-India Installation</h3>
              <p className="text-sm text-gray-400 mt-2 flex-1">Rapid on-site crews across metros and industrial belts.</p>
              <div className="flex items-center gap-1.5 mt-4 text-xs text-gray-500 font-heading font-semibold">
                {['MH', 'WB', 'RJ', 'DL', 'KA'].map((s) => <span key={s} className="px-2 py-1 rounded-md bg-primary/10 text-primary">{s}</span>)}
              </div>
            </div>
          </Reveal>

          {/* AMC schedule */}
          <Reveal delay={80}>
            <div className="h-full glass glass-hover rounded-3xl p-7 flex flex-col">
              <div className="flex items-center gap-2 text-xs font-heading font-bold uppercase tracking-wider text-gray-500"><CalendarClock className="w-4 h-4 text-primary" /> AMC Coverage</div>
              <h3 className="font-heading font-bold text-xl text-white mt-3">Lifetime Maintenance</h3>
              <div className="mt-4 space-y-2">
                {[['Quarterly Inspection', 'Scheduled'], ['Refilling & Testing', 'Included'], ['Emergency Callout', '24/7']].map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between text-sm">
                    <span className="text-gray-300">{k}</span><span className="text-primary font-semibold text-xs">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          {/* CTA card */}
          <Reveal delay={160} className="lg:col-span-2">
            <div className="relative h-full rounded-3xl p-8 overflow-hidden bg-gradient-to-br from-primary/90 to-accent/80 flex flex-col sm:flex-row sm:items-center justify-between gap-5">
              <div className="noise absolute inset-0 opacity-20" />
              <div className="relative">
                <div className="flex items-center gap-2 text-white/80 text-xs font-heading font-bold uppercase tracking-wider"><Wrench className="w-4 h-4" /> Free On-Site Audit</div>
                <h3 className="font-heading font-extrabold text-2xl md:text-3xl text-white mt-2">Book a free fire-safety consultation</h3>
                <p className="text-white/85 text-sm mt-2 max-w-md">Our experts assess your premises and recommend the right protection \u2014 at no cost.</p>
              </div>
              <Link to="/contact" className="relative shrink-0 inline-flex items-center gap-2 bg-white text-[#0b0f1a] font-heading font-bold px-6 py-3 rounded-xl hover:bg-gray-100 transition-colors">Get Started <ArrowUpRight className="w-4 h-4" /></Link>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
